import React, { Fragment, useCallback, useEffect, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import ReactModal from "react-modal";

import ai from "../../helpers/axios";
import TextInput from "../text-input";

const initialState = {
  createModalIsOpen: false,
  selectedVendor: null,
  name: "",
  saving: false,
  vendorOptions: [],
};

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged": {
      let value =
        action.fieldType === "number" ? parseInt(action.value) : action.value;

      return {
        ...state,
        [action.field]: value,
      };
    }

    case "resetState": {
      return {
        ...initialState,
      };
    }

    case "addVendorOption": {
      return {
        ...state,
        vendorOptions: [
          ...state.vendorOptions,
          action.value,
        ].sort(((a, b) => a.label?.localeCompare(b.label || ""))),
      };
    }

    default:
      break;
  }
  return state;
};

const VendorAutocomplete = (props) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const loadVendorOptions = useCallback(async () => {
    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });

    const response = await ai.auth(token).get("/api/trips/vendors", {
      params: {
        type: props.type,
      },
    }).catch((error) => {
      Sentry.captureException(error);
    });

    dispatch({
      type: "fieldChanged",
      field: "vendorOptions",
      value: (response?.data || []).map((vendor) => ({
        value: vendor._id,
        label: vendor.name,
      })),
    });
  }, [getAccessTokenSilently, loginWithRedirect, props.type]);

  useEffect(() => {
    loadVendorOptions();
  }, [loadVendorOptions]);

  const handleCreateOption = useCallback((inputValue) => {
    dispatch({
      type: "fieldChanged",
      field: "createModalIsOpen",
      value: true,
    });
    dispatch({
      type: "fieldChanged",
      field: "name",
      value: inputValue,
    });
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    dispatch({
      type: "fieldChanged",
      field: "createModalIsOpen",
      value: false,
    });
    dispatch({
      type: "fieldChanged",
      field: "name",
      value: "",
    });
  }, []);

  const handleTextInputChange = useCallback((e) => {
    dispatch({
      type: "fieldChanged",
      field: e.currentTarget.name,
      value: e.currentTarget.value,
    });
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!state.name) {
      return toast.error(
        "Please fill in all required fields and retry.",
        toastOptions,
      );
    }

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: true,
    });

    const token = await getAccessTokenSilently().catch((e) => {
      return loginWithRedirect();
    });
    const response = await ai.auth(token).post("/api/trips/vendors", {
      name: state.name,
      type: props.type,
    }).catch((error) => {
      Sentry.captureException(error);
    });

    dispatch({
      type: "fieldChanged",
      field: "saving",
      value: false,
    });

    if (response) {
      const vendor = response.data;

      dispatch({
        type: "addVendorOption",
        value: {
          value: vendor._id,
          label: vendor.name,
        },
      });

      handleCloseCreateModal();

      toast.success(
        `Vendor was successfully created. You can now select it in the builder.`,
        toastOptions,
      );
    }
  }, [getAccessTokenSilently, loginWithRedirect, props.type, state.name, handleCloseCreateModal]);

  return (
    <Fragment>
      <div className={`form-group ${props.containerClasses || ""}`}>
        <label>
          {props.label || "Vendor"}
          {props.required ? <span className="required"> *</span> : ""}
        </label>

        <CreatableSelect
          className="vendor-select"
          classNamePrefix="select"
          options={state.vendorOptions}
          placeholder={props.placeholder || "Select Vendor"}
          onChange={props.handleChange}
          isClearable={true}
          defaultOptions={state?.defaultOptions}
          value={props.selectedVendor}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          key={`selected-vendor-${props.selectedVendor?.value}`}
          onCreateOption={handleCreateOption}
          menuPosition="fixed"
        />
      </div>

      <ReactModal
        isOpen={state.createModalIsOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        onRequestClose={handleCloseCreateModal}
        style={{
          overlay: {
            zIndex: 999999,
            backgroundColor: "rgb(32 53 68 / 50%)",
          },
          content: {
            width: "300px",
            maxWidth: "30vw",
            maxHeight: "70vh",
            overflow: "scroll",
          },
        }}
        appElement={document.getElementById("app")}
      >
        <form
          className="base-form"
          id="createVendorModalForm"
          onSubmit={handleSubmit}
        >
          <h3>Create Vendor</h3>

          <TextInput
            name="name"
            label="Name"
            handleChange={handleTextInputChange}
            value={state.name}
            required
          />

          <div className="w-100 mt-20 d-flex gap-20">
            <button className="base-button evo-blue flex-half" type="button" onClick={handleCloseCreateModal}
                    disabled={state.saving}>
              Cancel
            </button>

            <button className="base-button btn-danger flex-half" type="button" onClick={handleSubmit}
                    disabled={state.saving}>
              Submit
            </button>
          </div>
        </form>
      </ReactModal>
    </Fragment>
  );
};

export default VendorAutocomplete;
